import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  BarChart2,
  Bot,
  Brain,
  Lock,
  Shield,
  Sparkles,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AI_SECTIONS,
  AiUsageSection,
  AiUsageSummary,
  formatCost,
  formatTokens,
  getAiUsage,
} from '@/lib/api/usageService';

// ── Per-section icon / colour mapping ────────────────────────────────────────

interface SectionMeta {
  icon: React.ElementType;
  color: string;
}

const SECTION_META: Record<string, SectionMeta> = {
  'deep-ai': { icon: Bot, color: '#6366f1' },
  'epa': { icon: Lock, color: '#0ea5e9' },
  'security-profile': { icon: Shield, color: '#22c55e' },
  'delta-ai-scan': { icon: Zap, color: '#f59e0b' },
  'ai-correlation': { icon: Brain, color: '#ec4899' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function pct(part: number, total: number): number {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TotalCard({ data }: { data: AiUsageSummary }) {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.06)})`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.info.main, 0.04)})`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1.2}>
          Project Total
        </Typography>

        {/* Stat row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} sx={{ mt: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={800}>
              {formatTokens(data.totalTokens)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Tokens
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary">
              ~{formatCost(data.totalEstimatedCostUsd)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Estimated Cost
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800}>
              {data.totalRuns}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Runs
            </Typography>
          </Box>
        </Stack>

        {/* Section breakdown chips */}
        {data.sections.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {data.sections.map((s) => {
              const meta = SECTION_META[s.section] ?? { icon: Sparkles, color: '#888' };
              const IconComp = meta.icon;
              const share = pct(s.totalTokens, data.totalTokens);
              return (
                <Tooltip
                  key={s.section}
                  title={`${formatTokens(s.totalTokens)} tokens (${s.runCount} run${s.runCount !== 1 ? 's' : ''})`}
                  arrow
                >
                  <Chip
                    size="small"
                    icon={<IconComp size={13} color={meta.color} />}
                    label={`${s.sectionLabel} ${share}%`}
                    sx={{
                      bgcolor: alpha(meta.color, 0.1),
                      color: meta.color,
                      fontWeight: 600,
                      fontSize: '0.72rem',
                      '& .MuiChip-icon': { ml: 0.5 },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function SectionCard({ section }: { section: AiUsageSection }) {
  const theme = useTheme();
  const meta = SECTION_META[section.section] ?? { icon: Sparkles, color: '#888' };
  const IconComp = meta.icon;

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent sx={{ p: 2.5 }}>
        {/* Section header */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
          <Box
            sx={{
              p: 0.9,
              borderRadius: 1.5,
              bgcolor: alpha(meta.color, 0.12),
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <IconComp size={20} color={meta.color} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>
              {section.sectionLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTokens(section.totalTokens)} tokens&nbsp;&nbsp;•&nbsp;&nbsp;
              Est. cost:&nbsp;<strong>~{formatCost(section.totalEstimatedCostUsd)}</strong>
              &nbsp;&nbsp;•&nbsp;&nbsp;
              {section.runCount} run{section.runCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Stack>

        {/* Records table */}
        {section.records.length > 0 ? (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(meta.color, 0.06) }}>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Date &amp; Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Model</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Tokens</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Est. Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {section.records.map((rec, i) => (
                  <TableRow key={rec.sk || i} hover>
                    <TableCell>
                      <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(rec.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rec.model}
                        size="small"
                        sx={{
                          fontSize: '0.68rem',
                          height: 20,
                          bgcolor: alpha(
                            rec.model.includes('opus')
                              ? theme.palette.warning.main
                              : theme.palette.info.main,
                            0.1
                          ),
                          color: rec.model.includes('opus')
                            ? theme.palette.warning.dark
                            : theme.palette.info.dark,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatTokens(rec.tokensUsed)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="primary" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        ~{formatCost(rec.estimatedCostUsd)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2.5,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.disabled">
              No usage recorded yet for this section.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function UsagePage() {
  const { appId } = useParams<{ appId: string }>();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AiUsageSummary | null>(null);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    setError(null);
    getAiUsage(appId)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || err.message || 'Failed to load usage data'))
      .finally(() => setLoading(false));
  }, [appId]);

  // Build the full section list (all five sections, even if no data yet)
  const sectionMap: Record<string, AiUsageSection> = {};
  for (const s of data?.sections ?? []) {
    sectionMap[s.section] = s;
  }
  const allSections: AiUsageSection[] = AI_SECTIONS.map((def) =>
    sectionMap[def.key] ?? {
      section: def.key,
      sectionLabel: def.label,
      totalTokens: 0,
      totalEstimatedCostUsd: 0,
      runCount: 0,
      records: [],
    }
  );

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={3}>
        {/* Page header */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
              }}
            >
              <BarChart2 size={24} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h5" fontWeight={700}>
              AI Usage
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Exact token consumption (as reported by the API) and estimated costs for all AI
            operations in this project. Costs are estimates based on model pricing.
          </Typography>
        </Box>

        {/* Loading state */}
        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        )}

        {/* Error state */}
        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}

        {/* Content */}
        {!loading && !error && data && (
          <>
            {/* Project total */}
            <TotalCard data={data} />

            {/* Per-section breakdown */}
            {allSections.map((section) => (
              <SectionCard key={section.section} section={section} />
            ))}
          </>
        )}

        {/* Empty state when data loaded but no records at all */}
        {!loading && !error && data && data.totalRuns === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No AI usage has been recorded yet. Usage data will appear here after the next scan runs.
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
